import { toCdnMediaUrl } from '../utils/cdnMediaUrl';

export function formatPrice(price) {
  if (price == null) return '$0.00';
  const value = typeof price === 'string' ? parseFloat(price) || 0 : price || 0;
  return `$${Number(value).toFixed(2)}`;
}

export function capitalizeLabel(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^x{1,3}l$/i.test(text)) return text.toUpperCase();
  return text
    .toLowerCase()
    .replace(/\b([a-z])/g, (letter) => letter.toUpperCase());
}

export function uniqueByLabel(items, getLabel, preferItem) {
  const seen = new Map();
  (items || []).forEach((item) => {
    const label = capitalizeLabel(getLabel(item));
    const key = label.toLowerCase();
    if (!key) return;
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, item);
      return;
    }
    if (preferItem?.(existing, item)) seen.set(key, item);
  });
  return Array.from(seen.values());
}

const SIZE_ORDER = [
  "xxxs",
  "xxs",
  "xs",
  "extra small",
  "s",
  "small",
  "m",
  "medium",
  "l",
  "large",
  "xl",
  "extra large",
  "xxl",
  "2xl",
  "xxxl",
  "3xl",
  "xxxxl",
  "4xl",
];

function sizeSortKey(size) {
  const label = getSizeLabel(size).toLowerCase().replace(/\s+/g, " ").trim();
  const named = SIZE_ORDER.indexOf(label);
  if (named >= 0) return [0, named, label];
  const compact = label.replace(/\s+/g, "");
  const compactNamed = SIZE_ORDER.indexOf(compact);
  if (compactNamed >= 0) return [0, compactNamed, label];
  const numeric = Number.parseFloat(label);
  if (Number.isFinite(numeric)) return [1, numeric, label];
  return [2, Number.POSITIVE_INFINITY, label];
}

export function sortSizes(sizes) {
  return [...(sizes || [])].sort((left, right) => {
    const [leftGroup, leftRank, leftLabel] = sizeSortKey(left);
    const [rightGroup, rightRank, rightLabel] = sizeSortKey(right);
    if (leftGroup !== rightGroup) return leftGroup - rightGroup;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return leftLabel.localeCompare(rightLabel);
  });
}

export function getSizeLabel(size) {
  if (!size) return '';
  let label = '';
  if (typeof size === 'string') label = size;
  else if (typeof size.size === 'string') label = size.size;
  else if (typeof size.size === 'object' && size.size?.size) label = size.size.size;
  else if (size.width && size.height) label = `${size.width}x${size.height} inches`;
  else {
    const raw = size.size;
    label = raw && typeof raw !== 'object' ? String(raw) : '';
  }
  return capitalizeLabel(label);
}

export function getColorLabel(color) {
  if (!color) return '';
  let label = '';
  if (typeof color === 'string') label = color;
  else if (typeof color === 'object') label = color.name || color.label || '';
  else label = String(color);
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(label.trim())) {
    if (/^#fff(fff)?$/i.test(label.trim())) return 'White';
    if (/^#000(000)?$/i.test(label.trim())) return 'Black';
  }
  return capitalizeLabel(label);
}

const NAMED_COLOR_SWATCHES = {
  black: '#000000',
  white: '#ffffff',
  red: '#e53935',
  blue: '#1e88e5',
  navy: '#1a237e',
  green: '#43a047',
  yellow: '#fdd835',
  orange: '#fb8c00',
  pink: '#ec407a',
  purple: '#8e24aa',
  violet: '#7e57c2',
  brown: '#6d4c41',
  gray: '#9e9e9e',
  grey: '#9e9e9e',
  ash: '#b2b2b2',
  silver: '#c0c0c0',
  gold: '#d4af37',
  beige: '#e8dcc8',
  cream: '#f5f0e6',
  natural: '#e6d5b8',
  khaki: '#c3b091',
  maroon: '#800000',
  teal: '#008080',
  cyan: '#00bcd4',
  magenta: '#d500f9',
  ivory: '#fffff0',
  charcoal: '#36454f',
};

function normalizeHex(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text)) return text;
  if (/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text)) return `#${text}`;
  return '';
}

/** CSS color for a product color swatch (12×12 circle). */
export function getColorSwatchValue(color) {
  if (!color) return '';
  if (typeof color === 'string') {
    const hex = normalizeHex(color);
    if (hex) return hex;
    const named = NAMED_COLOR_SWATCHES[color.trim().toLowerCase()];
    if (named) return named;
    return color.trim();
  }
  if (typeof color === 'object') {
    const fromFields = normalizeHex(
      color.hex ||
        color.color ||
        color.code ||
        color.color_code ||
        color.colorCode ||
        color.value ||
        color.rgb
    );
    if (fromFields) return fromFields;
    if (typeof color.rgb === 'string' && /^rgba?\(/i.test(color.rgb.trim())) {
      return color.rgb.trim();
    }
    const label = String(color.name || color.label || '').trim();
    const hexFromLabel = normalizeHex(label);
    if (hexFromLabel) return hexFromLabel;
    const named = NAMED_COLOR_SWATCHES[label.toLowerCase()];
    if (named) return named;
    if (label) return label;
  }
  return '';
}

export function productUnitPrice(product, size) {
  if (size?.price != null) return Number(size.price) || 0;
  if (typeof product?.price === 'number') return product.price;
  return parseFloat(String(product?.price || '0')) || 0;
}

function firstFiniteNumber(values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return 0;
}

export function productSoldCount(product) {
  return firstFiniteNumber([
    product?.sold,
    product?.sold_count,
    product?.total_sold,
    product?.totalSold,
    product?.sales,
    product?.sales_count,
    product?.quantity_sold,
    product?.order_count,
    product?.orders,
  ]);
}

export function productRatingValue(product) {
  return firstFiniteNumber([
    product?.rating,
    product?.average_rating,
    product?.avg_rating,
    product?.avgRating,
    product?.stars,
    product?.review_rating,
    product?.reviews_avg,
    product?.rating_average,
  ]);
}

export function sortShopProducts(products, sortBy) {
  const list = (products || []).map((product, index) => ({ product, index }));
  const nameOf = (product) => {
    if (typeof product?.name === 'string') return product.name;
    if (product?.name && typeof product.name === 'object') return product.name.name || 'Product';
    return 'Product';
  };
  const priceOf = (product) => productUnitPrice(product, product.product_sizes?.[0]);

  list.sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'low') cmp = priceOf(a.product) - priceOf(b.product);
    else if (sortBy === 'high') cmp = priceOf(b.product) - priceOf(a.product);
    else if (sortBy === 'a-z') cmp = nameOf(a.product).localeCompare(nameOf(b.product), undefined, { sensitivity: 'base' });
    else if (sortBy === 'z-a') cmp = nameOf(b.product).localeCompare(nameOf(a.product), undefined, { sensitivity: 'base' });
    else if (sortBy === 'highest-rated') cmp = productRatingValue(b.product) - productRatingValue(a.product);
    else if (sortBy === 'best') cmp = productSoldCount(b.product) - productSoldCount(a.product);
    if (cmp !== 0) return cmp;
    return a.index - b.index;
  });

  return list.map((item) => item.product);
}

export function cartArtUrl(item) {
  return String(item?.finalCanvasImage || item?.artUrl || "").trim();
}

export function cartLineKey(item) {
  const productId = String(item?.productId || item?.id || '');
  const size = String(item?.size || item?.dimensions || '').trim().toLowerCase();
  const color = String(item?.color || item?.variety || '').trim().toLowerCase();
  const art = cartArtUrl(item).toLowerCase();
  return `${productId}::${size}::${color}::${art}`;
}

export function findCartLine(items, candidate) {
  const key = cartLineKey(candidate);
  if (!key || key === '::') return null;
  return (items || []).find((item) => cartLineKey(item) === key) || null;
}

export function findCartItemForProduct(items, productId) {
  const key = String(productId || '');
  if (!key) return null;
  return (items || []).find((item) => String(item?.productId || item?.id || '') === key) || null;
}

export function cartQtyForProduct(items, productId) {
  return findCartItemForProduct(items, productId)?.quantity || 0;
}

export function cartTotal(items) {
  return (items || []).reduce((sum, item) => {
    const price =
      typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0'));
    return sum + price * (item.quantity || 0);
  }, 0);
}

export function cartCount(items) {
  return (items || []).length;
}

export function resolveCartItemType(sourceType, fallback = 'product') {
  const normalized = String(sourceType || '').trim().toLowerCase();
  if (!normalized) return fallback;
  if (normalized === 'instore-merchandise') return 'product';
  return String(sourceType || '').trim() || fallback;
}

export function buildCartItem(product, { quantity, size, color, artUrl } = {}) {
  const selectedSize = size || product.product_sizes?.[0] || null;
  const selectedColor = color || product.color?.[0] || null;
  const productId = product.id;
  const picture = toCdnMediaUrl(product.picture || product.imgURL || '') || undefined;
  const resolvedArt = artUrl ? toCdnMediaUrl(artUrl) : undefined;
  return {
    id: productId,
    productId,
    productSizeId: selectedSize?.id,
    name:
      typeof product.name === 'string' ? product.name : String(product.name?.name || 'Product'),
    type: resolveCartItemType(product.type),
    quantity: quantity || 1,
    price: productUnitPrice(product, selectedSize),
    imageUrl: picture,
    imgURL: picture,
    finalCanvasImage: resolvedArt,
    artUrl: resolvedArt,
    size: getSizeLabel(selectedSize) || undefined,
    dimensions: getSizeLabel(selectedSize) || undefined,
    variety: getColorLabel(selectedColor) || undefined,
    color: getColorLabel(selectedColor) || undefined,
    sku_code: product.sku_code,
    placement: product.artwork_placement || product.artworkPlacement || product.print_area,
  };
}

export function buildPackCartItem(pack, { quantity = 1, artUrl, selections, products } = {}) {
  const packId = pack?.id;
  const unitPrice =
    typeof pack?.price === 'number'
      ? pack.price
      : parseFloat(String(pack?.pack_price ?? pack?.price ?? '0')) || 0;
  const picture =
    toCdnMediaUrl(pack?.picture || pack?.image || pack?.imgURL || "") ||
    undefined;
  const resolvedArt = artUrl ? toCdnMediaUrl(artUrl) : undefined;
  return {
    id: packId,
    productId: packId,
    name: typeof pack?.name === 'string' ? pack.name : String(pack?.name || 'Pack'),
    type: 'pack',
    isPack: true,
    quantity: Math.max(1, Number(quantity) || 1),
    price: unitPrice,
    imageUrl: picture,
    imgURL: picture,
    finalCanvasImage: resolvedArt,
    artUrl: resolvedArt,
    sku_code: pack?.sku_code,
    products: Array.isArray(products) ? products : [],
    packSelections: selections || undefined,
  };
}

export function buildPaymentTempPayload(
  cartItems,
  { artUrl, currency = 'USD', pricing, userInfo, userId } = {}
) {
  const items = (cartItems || []).map((item) => {
    const variety =
      typeof item.variety === 'object' && item.variety
        ? String(item.variety.name || '')
        : item.variety;
    const rawImageUrl =
      (typeof item.finalCanvasImage === "string" && item.finalCanvasImage.trim()) ||
      (typeof item.artUrl === "string" && item.artUrl.trim()) ||
      (typeof item.imageUrl === "string" && item.imageUrl.trim()) ||
      (typeof item.imgURL === "string" && item.imgURL.trim()) ||
      (typeof artUrl === "string" && artUrl.trim()) ||
      "";
    const imageUrl = rawImageUrl ? toCdnMediaUrl(rawImageUrl) : undefined;
    const price =
      typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')) || 0;
    const base = {
      id: item.productId || item.id,
      productId: item.productId || item.id,
      productSizeId: item.productSizeId,
      name: typeof item.name === 'string' ? item.name : String(item.name || 'Product'),
      type: resolveCartItemType(item.type),
      quantity: item.quantity,
      price,
      size: item.size,
      color: item.color || variety,
      variety,
      sku_code: item.sku_code,
      image_url: imageUrl,
    };
    if (item.isPack || String(item.type || '').toLowerCase() === 'pack') {
      const products = Array.isArray(item.products) ? item.products : [];
      return {
        ...base,
        type: 'pack',
        isPack: true,
        products: products.map((product) => ({
          id: product.id,
          productId: product.productId || product.id,
          productSizeId: product.productSizeId,
          name: product.name,
          type: product.type || 'product',
          quantity: product.quantity,
          price: product.price,
          unit_price: product.unit_price ?? product.price,
          size: product.size,
          inches: product.inches,
          color: product.color || product.variety,
          variety: product.variety || product.color,
          sku_code: product.sku_code,
          image_url: toCdnMediaUrl(
            product.imageUrl || product.picture || ""
          ) || undefined,
          units: product.units,
        })),
      };
    }
    return base;
  });

  const lineSubtotal = parseFloat(
    items.reduce((sum, item) => sum + Number(item.price || 0) * (item.quantity || 0), 0).toFixed(2)
  );
  const totalAmount =
    pricing && Number.isFinite(pricing.total) ? pricing.total : lineSubtotal;

  return {
    items,
    totalAmount,
    currency,
    ...(pricing
      ? {
          subtotal: pricing.subtotal,
          processingFee: pricing.processingFee,
          salesTax: pricing.salesTax,
          processingFeePercent: pricing.processingFeePercent,
          salesTaxPercent: pricing.salesTaxPercent,
        }
      : {}),
    ...(userId ? { userId, user_id: userId } : {}),
    ...(userInfo && Object.keys(userInfo).length ? { userInfo } : {}),
  };
}

export function uniqueCartArtUrls(items) {
  const seen = new Set();
  const urls = [];
  (items || []).forEach((item) => {
    const url = String(item?.finalCanvasImage || item?.artUrl || "").trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  });
  return urls;
}
